import { useState, useEffect } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  Cascader,
  Table,
  TableColumnProps,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { SMessage, STags } from 'shj-components'
import { useRequest, useSetState } from 'ahooks'
import {
  querySkuPricePage,
  productSearchList,
  findProdCateTreeList,
  getBrandList,
  getSpecialCodesListNew,
  getBatchSize,
  priceExportRecord,
  exportQuerySkuPriceExcel,
  getAuthSkuCount
} from '@/services/product.ts'
import { httpCompanyList } from '@/services/homeService.ts'
import { dateFormat } from '@/utils/date.ts'
import { SAlert } from 'shj-components'
import dayjs from 'dayjs'
import ExportModal from './exportModal';

const { RangePicker } = DatePicker
const inputWidth = '200px'

const enumMap = {
  // 2. 计算方式值说明
  STANDARD: '直接定价（价格导入）',
  GENERAL_ITEM: '直接定价',
  CALCULATE: '利率计算',
  // 3. 价格类型
  PURCHASE_PRICE: '采购价',
  MARKET_PRICE: '市场价',
  PROJECT_MANAGER_PRICE: '项目经理价',
  CUSTOMER_PRICE: '客户价',
  SEND_PACK_PRICE: '发包价',
  SALE_PRICE: '销售指导价',
}

/**
 * description:
 * dateTime: 2025/3/20-下午5:53
 * author: ZhangJun 1580043700@qq.com
 */
const MoneySearch = () => {
  const [state, setState]: any = useSetState({ error: [], dataSource: [], total: 0 })
  const [form] = Form.useForm()
  const { data: companyList, loading: loadingCompany }: any = useRequest(httpCompanyList)
  const { data: productList, loading: loadingProduct }: any = useRequest(productSearchList, {
    defaultParams: [{ prodType: 'PRODUCT_PACKAGE' }],
  })
  const { data: cateTreeList, loading: loadingCateTree }: any = useRequest(findProdCateTreeList, {
    defaultParams: [{ 
      enabled: true,
      modeFlags: [4],
     }],
  })
  const { data: brandList, loading: loadingBrand }: any = useRequest(getBrandList, {
    defaultParams: [{}],
  })
  const { data: specialCodeList, loading: loadingSpecialCode }: any = useRequest(getSpecialCodesListNew, {
    defaultParams: [{}],
  })
  const { loading, runAsync: runAsyncTable }: any = useRequest(querySkuPricePage, {
    manual: true,
  })
  const { countLoading, runAsync: runAsyncCount }: any = useRequest(getAuthSkuCount, {
    manual: true,
  })
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [limitNumber, setLimitNumber] = useState(5000);
  const [openExportModal, setOpenExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const columns: Array<TableColumnProps> = [
    {
      title: '产品SKU名称',
      dataIndex: ['sku','skuName'],
      width: 230,
      fixed: 'left',
      render: text => (
        <Tooltip title={text}>
          <Typography.Text ellipsis={true}>{text}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: '产品采购价(元)',
      dataIndex: ['priceInfo', 'purchasePrice'],
      width: 140,
      ellipsis: true,
    },
    { title: '市场价(元)', dataIndex: ['priceInfo', 'marketPrice'], width: 140, ellipsis: true },
    {
      title: '项目经理价(元)',
      dataIndex: ['priceInfo', 'projectManagerPrice'],
      width: 130,
      ellipsis: true,
    },
    {
      title: '项目经理价取值来源',
      dataIndex: ['priceInfo', 'projectManagerPriceType'],
      width: 180,
      ellipsis: true,
      render: text => enumMap[text] ?? '',
    },
    {
      title: '项目经理利率(%)',
      dataIndex: ['priceInfo', 'projectManagerPriceInfo', 'rate'],
      width: 150,
      ellipsis: true,
      render: (text, record) => <span>{ record?.priceInfo?.projectManagerPriceType === 'STANDARD' ? '' : record?.projectManagerPriceInfo?.defaultRateFlag ? '默认' + text : text }</span>
    },
    { title: '客户价(元)', dataIndex: ['priceInfo', 'customerPrice'], width: 140, ellipsis: true },
    {
      title: '客户价取值来源',
      dataIndex: ['priceInfo', 'customerPriceType'],
      width: 180,
      ellipsis: true,
      render: text => enumMap[text] ?? '',
    },
    {
      title: '毛利率（%）',
      dataIndex: ['priceInfo', 'customerPriceInfo', 'rate'],
      width: 120,
      ellipsis: true,
      render: (text, record) => <span>{ record?.priceInfo?.customerPriceType === 'STANDARD' ? '' : text }</span>
    },
    { title: '发包价(元)', dataIndex: ['priceInfo', 'sendPackPrice'], width: 140, ellipsis: true },
    {
      title: '发包价取值来源',
      dataIndex: ['priceInfo', 'sendPackPriceInfo', 'type'],
      width: 180,
      ellipsis: true,
      render: text => enumMap[text] ?? '',
    },
    {
      title: '提点率(%)',
      dataIndex: ['priceInfo', 'sendPackPriceInfo', 'rate'],
      width: 120,
      ellipsis: true,
      render: (text, record) => <span>{ record?.priceInfo?.sendPackPriceInfo?.type === 'STANDARD' ? '' : text }</span>
    },
    {
      title: '产品SKU_ID',
      dataIndex: 'skuId',
      width: 200,
      ellipsis: true,
      render: text => (
        <Tooltip title={text}>
          <Typography.Text ellipsis={true}>{text}</Typography.Text>
        </Tooltip>
      ),
    },
    { title: '品牌', dataIndex: ['sku', 'brandName'], width: 120, ellipsis: true },
    { title: '产品类型', dataIndex: ['sku', 'prodTypeName'], width: 120, ellipsis: true },
    { title: '特项代码', dataIndex: ['sku', 'specialCode'], width: 120, ellipsis: true },
    {
      title: '物料/施工SKU编码',
      dataIndex: ['sku', 'sourceCode'],
      width: 200,
      ellipsis: true,
      render: text => (
        <Tooltip title={text}>
          <Typography.Text ellipsis={true}>{text}</Typography.Text>
        </Tooltip>
      ),
    },
    { title: '产品创建时间', dataIndex: ['sku', 'createdTime'], width: 200, ellipsis: true, render: (text) => <span>{ dayjs(text)?.format('YYYY-MM-DD HH:mm:ss')}</span> },
    { title: '产品分类', dataIndex: ['sku', 'prodCateName'], width: 120, ellipsis: true },
    {
      title: 'SKU状态',
      dataIndex: ['sku', 'enabled'],
      width: 120,
      ellipsis: true,
      render: (text, record) => <STags
        itemKey=""
        label={text ? '启用' : '禁用'}
        fontColor={text ? '#00A865' : '#EB3723'}
        bgColor={text ? '#D7F7EA' : '#FFEAE8'}
      />
    },
    {
      title: '报错信息',
      dataIndex: ['priceInfo', 'errorMessages'],
      width: 550,
      render: (error: any) => (<div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        <Tooltip
          overlayStyle={{ maxWidth:600 }}
          title={
            <>
              {error?.map(item => (<div>{ item.errorMessage }</div>))}
            </>
          }
        >
          <span style={{ color: 'red' }}>{error?.map(item => item.errorMessage)?.join(',')}</span>
        </Tooltip>
      </div>),
    },
  ]

  useEffect(() => {
    getLimitSize()
  }, [])

  const getLimitSize = async () => {
    const res = await getBatchSize()
    if (typeof res === 'number') {
      setLimitNumber(res);
    }
  }

  const handleChangePage = (page, pageSize) => {
    onSearch(page, pageSize);
    setCurrentPageSize(pageSize);
  }

  const onSearch = (page, pageSize = 10) => {
    form.validateFields().then(values => {
      console.log(values);
      let prodCascadeCateIds: any = [];
      let createdTimeStartWith = '';
      let createdTimeEndWith = '';
      if (values?.prodCateIdList?.length) {
        values?.prodCateIdList?.forEach((list: any) => {
          prodCascadeCateIds = [...prodCascadeCateIds, list?.[list?.length - 1]]
        })
      }
      if (values?.createdTime?.length) {
        createdTimeStartWith = dateFormat(values?.createdTime?.[0], 'YYYY-MM-DD HH:mm:ss') || '',
        createdTimeEndWith = dateFormat(values?.createdTime?.[1], 'YYYY-MM-DD HH:mm:ss') || ''
      }
      setCurrentPage(page);
      runAsyncTable({
        params: {
          pageId: page,
          pageSize,
        },
        data: {
          priceQuery: {
            currentDate: dateFormat(values.currentDate, 'YYYY-MM-DD'),
            productId: values?.productId,
            companyId: values?.companyId,
          },
          skuQuery: {
            ...values,
            prodCascadeCateIds,
            createdTimeStartWith,
            createdTimeEndWith,
            productId: null,
            companyId: null,
          },
        },
        showError: false,
      })
        .then(result => {
          const list = result?.list || []
          const total = result?.count || 0;
          setState({
            dataSource: list,
            total,
          })
        })
        .catch(() => {
          setState({
            dataSource: [],
          })
        })
    })
  }

  const filterOption = (input, option) => {
    return ((option?.companyName || option?.prodName) ?? '')
      .toLowerCase()
      .includes(input.toLowerCase())
  }

  const handleOpenExportModal = () => {
    setOpenExportModal(true)
  }

  const exportData = async () => {
    form.validateFields().then(async (values) => {
      console.log(values);
      let totalNumber = 0;
      let prodCascadeCateIds: any = [];
      let createdTimeStartWith = '';
      let createdTimeEndWith = '';
      if (values?.prodCateIdList?.length) {
        values?.prodCateIdList?.forEach((list: any) => {
          prodCascadeCateIds = [...prodCascadeCateIds, list?.[list?.length - 1]]
        })
      }
      if (values?.createdTime?.length) {
        createdTimeStartWith = dateFormat(values?.createdTime?.[0], 'YYYY-MM-DD HH:mm:ss') || '',
        createdTimeEndWith = dateFormat(values?.createdTime?.[1], 'YYYY-MM-DD HH:mm:ss') || ''
      }
      const res = await runAsyncCount({
        data: {
          ...values,
          prodCascadeCateIds,
          createdTimeStartWith,
          createdTimeEndWith,
          currentDate: dateFormat(values.currentDate, 'YYYY-MM-DD'),
          productId: null,
          companyId: null,
          prodTypes: ['PRODUCT_SINGLE', 'PRODUCT_BASIC_ITEM'],
        }
      })
      if (res && typeof res === 'string') {
        totalNumber = Number(res);
      }
      if (limitNumber < totalNumber) {
        SMessage.confirm({
          title: `总数据量${totalNumber}条，需要分批导出`,
          content: `超出${limitNumber}条需要分批导出`,
          okText: '分批导出',
          onOk: async () => {
            const values = form.getFieldsValue();
            let prodCascadeCateIds: any = [];
            let createdTimeStartWith = '';
            let createdTimeEndWith = '';
            if (values?.prodCateIdList?.length) {
              values?.prodCateIdList?.forEach((list: any) => {
                prodCascadeCateIds = [...prodCascadeCateIds, list?.[list?.length - 1]]
              })
            }
            if (values?.createdTime?.length) {
              createdTimeStartWith = dateFormat(values?.createdTime?.[0], 'YYYY-MM-DD HH:mm:ss') || '',
              createdTimeEndWith = dateFormat(values?.createdTime?.[1], 'YYYY-MM-DD HH:mm:ss') || ''
            }
            const res = await priceExportRecord({
              priceQuery: {
                currentDate: dateFormat(values.currentDate, 'YYYY-MM-DD'),
                productId: values?.productId,
                companyId: values?.companyId,
              },
              skuQuery: {
                ...values,
                prodCascadeCateIds,
                createdTimeStartWith,
                createdTimeEndWith,
                productId: null,
                companyId: null,
              },
            })
            if (Array.isArray(res) && res?.length) {
              message.success('分批成功');
              setTimeout(() => {
                setOpenExportModal(true);
              }, 500)
            }
          },
        })
      } else {
        const values = form.getFieldsValue();
        let prodCascadeCateIds: any = [];
        let createdTimeStartWith = '';
        let createdTimeEndWith = '';
        if (values?.prodCateIdList?.length) {
          values?.prodCateIdList?.forEach((list: any) => {
            prodCascadeCateIds = [...prodCascadeCateIds, list?.[list?.length - 1]]
          })
        }
        if (values?.createdTime?.length) {
          createdTimeStartWith = dateFormat(values?.createdTime?.[0], 'YYYY-MM-DD HH:mm:ss') || '',
          createdTimeEndWith = dateFormat(values?.createdTime?.[1], 'YYYY-MM-DD HH:mm:ss') || ''
        }
        setExportLoading(true);
        await exportQuerySkuPriceExcel({
          priceQuery: {
            currentDate: dateFormat(values.currentDate, 'YYYY-MM-DD'),
            productId: values?.productId,
            companyId: values?.companyId,
          },
          skuQuery: {
            ...values,
            prodCascadeCateIds,
            createdTimeStartWith,
            createdTimeEndWith,
            productId: null,
            companyId: null,
          },
        })
        setExportLoading(false);
      }
    })
  }

  const handleRefresh = () => {
    form.resetFields()
  }

  return (
    <div style={{ backgroundColor: 'white', padding: '10px' }}>
      <div>
        {/* <div style={{padding: '10px', backgroundColor: 'cornsilk'}}>
                    <Typography.Text>当前仅支持单品、基础项SKU查询，不支持组合SKU查询。</Typography.Text>
                </div> */}
        <SAlert type="warning">当前仅支持单品、基础项SKU查询，不支持组合SKU查询。</SAlert>
        <Form layout="inline" form={form} style={{ marginTop: '20px' }}>
          <Space wrap size={[10, 20]}>
            <Form.Item
              label="适用产品"
              name="productId"
              required
              rules={[{ required: true, message: '请选择适用产品' }]}
            >
              <Select
                showSearch
                style={{ width: inputWidth }}
                loading={loadingProduct}
                options={productList}
                filterOption={filterOption}
                fieldNames={{ label: 'prodName', value: 'id' }}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="适用门店"
              name="companyId"
              required
              rules={[{ required: true, message: '请选择适用门店' }]}
            >
              <Select
                showSearch
                style={{ width: inputWidth }}
                loading={loadingCompany}
                options={companyList}
                filterOption={filterOption}
                optionFilterProp="label"
                fieldNames={{ label: 'companyName', value: 'id' }}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="日期"
              name="currentDate"
              required
              rules={[{ required: true, message: '请选择日期' }]}
            >
              <DatePicker style={{ width: inputWidth }} />
            </Form.Item>
            <Form.Item
              label="产品SKU_ID"
              name="id"
            >
              <Input allowClear style={{ width: inputWidth }} />
            </Form.Item>
            <Form.Item
              label={'产品分类'}
              name={'prodCateIdList'}
            >
              <Cascader
                options={cateTreeList}
                placeholder="请选择分类"
                fieldNames={{
                  value: 'id',
                  label: 'cateName',
                }}
                changeOnSelect
                loading={loadingCateTree}
                style={{ width: inputWidth }}
                showSearch
                multiple
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="产品类型"
              name="prodType"
            >
              <Select
                style={{ width: inputWidth }}
                options={[{
                  label: '单品',
                  value: 'PRODUCT_SINGLE'
                }, {
                  label: '基础项',
                  value: 'PRODUCT_BASIC_ITEM'
                }]}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="sku状态"
              name="enabled"
            >
              <Select
                style={{ width: inputWidth }}
                options={[{
                  label: '启用',
                  value: true
                }, {
                  label: '禁用',
                  value: false
                }]}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="品牌"
              name="brandId"
            >
              <Select
                style={{ width: inputWidth }}
                options={brandList || []}
                loading={loadingBrand}
                showSearch
                optionFilterProp="name"
                fieldNames={{ value: 'id', label: 'name' }}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="特项代码"
              name="specialCode"
            >
              <Select
                style={{ width: inputWidth }}
                options={specialCodeList?.map((item: any) => {
                  return { label: item.code + '-' + item.name, value: item.code }
                })}
                loading={loadingSpecialCode}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="物料/施工SKU编码"
              name="sourceCode"
            >
              <Input allowClear style={{ width: inputWidth }} />
            </Form.Item>
            <Form.Item
              label="产品创建时间"
              name="createdTime"
            >
              <RangePicker
                style={{width: '100%'}}
                showTime={{
                  hideDisabledOptions: true,
                  defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
                }}
                allowClear
                placeholder={['开始时间', '结束时间']}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" onClick={() => onSearch(1, currentPageSize)}>
                查询价格
              </Button>
            </Form.Item>
            <Form.Item>
              <Button onClick={() => handleRefresh()}>
                重置
              </Button>
            </Form.Item>
          </Space>
        </Form>
      </div>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={() => handleOpenExportModal()} style={{ marginRight: '10px' }}>
          分批导出记录
        </Button>
        <Button type="primary" loading={exportLoading} onClick={() => exportData()}>
          导出
        </Button>
      </div>
      <div style={{ marginTop: '20px' }}>
        <Table
          columns={columns}
          style={{ width: '100%' }}
          virtual
          dataSource={state.dataSource}
          loading={loading}
          pagination={{ total: state.total ?? 0, onChange: handleChangePage, current: currentPage, showQuickJumper: true, showSizeChanger: true, showTotal: (total) => <span>共{ total }条数据</span> }}
        />
      </div>
      {
        openExportModal && <ExportModal open={openExportModal} setOpen={(status: any) => setOpenExportModal(status)} />
      }
    </div>
  )
}

export default MoneySearch
